import { motion } from "framer-motion";
import { CheckCircle2, Eye, Layout, Zap, Shield, Users } from "lucide-react";
import { useState } from "react";
import { ContactModal } from "@/components/ContactModal";

const UXDesignServices = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <main className="min-h-screen bg-gradient-to-b from-background to-accent-navy/30">
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            UI/UX Design Services
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-muted-foreground mb-8"
          >
            Transform user experiences with our expert UI/UX design services. Research-driven design that converts users into customers. From concept to pixel-perfect interfaces.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Start Your Design Project
          </motion.button>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Our UI/UX Design Process
          </motion.h2>
          <div className="space-y-6">
            {[
              { step: 1, title: "User Research", desc: "Deep understanding of user needs, behaviors, and pain points through interviews and testing." },
              { step: 2, title: "Information Architecture", desc: "Structuring content and navigation for optimal user flow and discoverability." },
              { step: 3, title: "Wireframing", desc: "Low-fidelity layouts to establish structure and user journey before visual design." },
              { step: 4, title: "Visual Design", desc: "Creating beautiful, on-brand interfaces with attention to typography and color." },
              { step: 5, title: "Prototyping", desc: "Interactive prototypes to test and validate design decisions before development." },
              { step: 6, title: "Testing & Iteration", desc: "Usability testing and refinement based on real user feedback." },
              { step: 7, title: "Design Handoff", desc: "Comprehensive documentation and assets for smooth development handoff." },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4 items-start"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-coral/20 flex items-center justify-center text-coral font-bold">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services We Offer */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            UI/UX Design Services We Offer
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Users, title: "User Research", desc: "In-depth user interviews, surveys, and persona development." },
              { icon: Layout, title: "Wireframing", desc: "Low-fidelity layouts to establish structure and user flows." },
              { icon: Eye, title: "Visual Design", desc: "Pixel-perfect interfaces with attention to brand consistency." },
              { icon: Zap, title: "Prototyping", desc: "Interactive prototypes for testing and validation." },
              { icon: Shield, title: "Usability Testing", desc: "Comprehensive testing to identify and fix UX issues." },
              { icon: Layout, title: "Design Systems", desc: "Component libraries and style guides for consistency." },
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <service.icon className="w-8 h-8 text-coral mb-4" />
                <h3 className="font-semibold text-lg mb-2">{service.title}</h3>
                <p className="text-muted-foreground">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* User Research */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            User Research & Testing Expertise
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { method: "User Interviews", desc: "One-on-one interviews to understand user needs and motivations." },
              { method: "Usability Testing", desc: "Observational testing to identify UX issues and improvements." },
              { method: "A/B Testing", desc: "Data-driven design decisions through controlled experiments." },
              { method: "Persona Development", desc: "Detailed user personas to guide design decisions." },
              { method: "Journey Mapping", desc: "Visualizing user experiences across touchpoints." },
              { method: "Card Sorting", desc: "Optimizing information architecture based on user behavior." },
            ].map((research, index) => (
              <motion.div
                key={research.method}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-4 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-lg mb-1">{research.method}</h3>
                <p className="text-muted-foreground">{research.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Design Systems */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Design System Development
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Layout, title: "Component Libraries", desc: "Reusable UI components for consistent design." },
              { icon: Eye, title: "Style Guides", desc: "Comprehensive documentation for design standards." },
              { icon: Zap, title: "Pattern Libraries", desc: "Documented design patterns for common interactions." },
              { icon: Shield, title: "Design Tokens", desc: "Systematic design values for scalability." },
            ].map((system, index) => (
              <motion.div
                key={system.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <system.icon className="w-8 h-8 text-coral mb-4" />
                <h3 className="font-semibold text-lg mb-2">{system.title}</h3>
                <p className="text-muted-foreground">{system.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Accessibility */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Accessibility & Inclusive Design
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Shield, title: "WCAG Compliance", desc: "Adherence to Web Content Accessibility Guidelines." },
              { icon: Eye, title: "Screen Reader Optimization", desc: "Designs that work with assistive technologies." },
              { icon: Layout, title: "Color Contrast", desc: "Optimized color ratios for readability." },
              { icon: Zap, title: "Keyboard Navigation", desc: "Full keyboard accessibility for all interactions." },
            ].map((access, index) => (
              <motion.div
                key={access.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <access.icon className="w-8 h-8 text-coral mb-4" />
                <h3 className="font-semibold text-lg mb-2">{access.title}</h3>
                <p className="text-muted-foreground">{access.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            UI/UX Design Pricing & Packages
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Project-Based", price: "$2,999+", features: ["Complete UX project", "User research", "Wireframes", "Visual design", "Prototypes"] },
              { name: "Hourly Rate", price: "$75-$150/hr", features: ["Flexible engagement", "Expert consultation", "Design reviews", "Quick iterations", "Specialized expertise"] },
              { name: "Retainer", price: "$5,000+/mo", features: ["Ongoing design support", "Priority access", "Design system maintenance", "Team integration", "Strategic guidance"] },
            ].map((pkg, index) => (
              <motion.div
                key={pkg.name}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <h3 className="font-semibold text-xl mb-2">{pkg.name}</h3>
                <div className="text-3xl font-bold text-coral mb-4">{pkg.price}</div>
                <ul className="space-y-2">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-coral" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8 text-center"
          >
            Why Choose Koech Creatives for UI/UX Design
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Users, title: "Research-Driven", desc: "Data-driven design decisions based on user research and testing." },
              { icon: Eye, title: "Cross-Platform Expertise", desc: "Consistent experiences across web, mobile, and beyond." },
              { icon: Shield, title: "Accessibility Focus", desc: "Inclusive design that works for all users." },
              { icon: Zap, title: "Measurable Outcomes", desc: "Design improvements that drive real business results." },
            ].map((reason, index) => (
              <motion.div
                key={reason.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4"
              >
                <reason.icon className="w-8 h-8 text-coral flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-lg mb-1">{reason.title}</h3>
                  <p className="text-muted-foreground">{reason.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-4"
          >
            Ready to Transform Your User Experience?
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-muted-foreground mb-8"
          >
            Let's create interfaces that users love and that drive business results.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Get a Free Consultation
          </motion.button>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </main>
  );
};

export default UXDesignServices;
